import React, { useState, ChangeEvent } from 'react'
import TextField from '@mui/material/TextField'
import styles from './styles.module.css'
import { WorkDayDropDown } from '../custom-inputs/CustomInputs'
import { initialDaySettingValues } from '../utils'
import { fetchData } from '../../../../../global/utils/fetch'
import { createDaySetting } from '../../api'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'
import { IDaySettingPartial } from '../../../../../../../app/entities/DaySetting'

interface IAddUniqueDayProps {
  cancel: React.MouseEventHandler
  technicianData: ITechnicianPartial
  isDefaultWorkDay: boolean
  isDefaultLunch: boolean
  refetchTechnicianData: Function
  technicianObjectId: string | undefined
}

export interface INewDaySetting extends IDaySettingPartial {
  dayDateFrom: string
  dayDateTo: string
}

const nonWorkingDayTextFieldStyles = {
  width: 130,
}
const workingDayTextFieldStyles = {
  width: 130,
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'black',
  },
}

export default function AddUniqueDay(props: IAddUniqueDayProps) {
  const {
    refetchTechnicianData,
    technicianObjectId,
    technicianData,
    isDefaultLunch,
    isDefaultWorkDay,
  } = props

  const [newDaySetting, setNewDaySetting] = useState<INewDaySetting>(
    initialDaySettingValues,
  )
  const [isWorkingDay, setIsWorkingDay] = useState<boolean>(false)
  const { lunchDuration, workDayFrom, workDayTo, location } = technicianData

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    if (name === 'dayDate') {
      //this is when you 'edit' an existing day setting
      setNewDaySetting({
        ...newDaySetting,
        dayDateFrom: value,
        dayDateTo: value,
        day: new Date(Date.parse(value)),
      })
    } else if (name === 'lunchDuration') {
      setNewDaySetting({
        ...newDaySetting,
        lunchDuration: value ? Number(value) : undefined,
      })
    } else {
      setNewDaySetting({ ...newDaySetting, [name]: value })
    }
  }

  async function handleWorkDay(value: boolean) {
    if (value) {
      setIsWorkingDay(true)

      const locationDefaultWorkDayFrom = location?.workDayFrom
      const locationDefaultWorkDayTo = location?.workDayTo
      const locationDefaultLunchDuration = location?.lunchDuration

      const lunchDurationValue = isDefaultLunch
        ? locationDefaultLunchDuration
        : lunchDuration
      const startTimeFromValue = isDefaultWorkDay
        ? locationDefaultWorkDayFrom
        : workDayFrom
      const startTimeToValue = isDefaultWorkDay
        ? locationDefaultWorkDayTo
        : workDayTo

      setNewDaySetting({
        ...newDaySetting,
        workDay: value,
        startTime: startTimeFromValue,
        endTime: startTimeToValue,
        lunchDuration: lunchDurationValue,
      })
    } else {
      setIsWorkingDay(false)

      setNewDaySetting({
        ...newDaySetting,
        workDay: value,
      })
    }
  }
  function cleanDaySetting(daySetting: INewDaySetting) {
    // Clean the day setting of some fields if it is a non-working day
    if (!daySetting.workDay) {
      daySetting.lunchDuration = undefined
      daySetting.startTime = undefined
      daySetting.endTime = undefined
    }

    return daySetting
  }

  async function addSingleDaySetting() {
    try {
      await fetchData(
        createDaySetting(cleanDaySetting(newDaySetting), technicianObjectId),
      )
      await refetchTechnicianData()
    } catch (err) {
      alert('error adding day settings: ' + err)
      console.error(err)
    }
  }
  function generateDateRangeArray(startDate: number, endDate: number) {
    const ONE_DAY_IN_MS = 86400000
    const totalNumOfDays = Math.round(
      (endDate - startDate + ONE_DAY_IN_MS) / ONE_DAY_IN_MS,
    )
    const dateRangeArray: any[] = []

    let nextDay = startDate
    for (let i = 0; i < totalNumOfDays; i++) {
      let newDaySettingCopy = { ...newDaySetting, day: nextDay }
      dateRangeArray.push(newDaySettingCopy)
      nextDay += ONE_DAY_IN_MS
    }
    return dateRangeArray
  }

  async function addRangeDaySettings(startDate: number, endDate: number) {
    const dateRangeArray = generateDateRangeArray(startDate, endDate)
    const createdDaySettingPromise = dateRangeArray.map((daySetting: any) =>
      fetchData(
        createDaySetting(cleanDaySetting(daySetting), technicianObjectId),
      ),
    )

    try {
      await Promise.all(createdDaySettingPromise)
      if (refetchTechnicianData) {
        refetchTechnicianData()
      }
    } catch (err) {
      alert(err)
    }
  }

  async function addDaySettings() {
    const { dayDateFrom, dayDateTo, reason } = newDaySetting
    if (!reason) {
      alert('Missing fields required')
      return
    }
    if (dayDateFrom && dayDateTo) {
      const dayDateFromParsed = Date.parse(dayDateFrom)
      const dayDateToParsed = Date.parse(dayDateTo)

      if (dayDateFromParsed > dayDateToParsed) {
        alert('The range Start day should be less than the End one')
      } else if (dayDateFromParsed === dayDateToParsed) {
        await addSingleDaySetting()
      } else {
        await addRangeDaySettings(dayDateFromParsed, dayDateToParsed)
      }
    }
  }

  return (
    <tr className={`${styles.bodyRow} ${styles.addNewRow}`}>
      <td className={`${styles.bodyRowCell}`}>
        <TextField
          onChange={handleChange}
          value={newDaySetting.dayDateFrom}
          InputLabelProps={{ shrink: true }}
          type='Date'
          label='From'
          name='dayDate'
          sx={{ width: 130 }}
        />
        <TextField
          onChange={handleChange}
          value={newDaySetting.dayDateTo}
          InputLabelProps={{ shrink: true }}
          type='Date'
          label='To'
          name='dayDateTo'
          sx={{ width: 130 }}
        />
      </td>
      <td className={styles.bodyRowCell}>
        <WorkDayDropDown
          value={newDaySetting.workDay ?? false}
          onChange={handleWorkDay}
        />
      </td>
      <td className={styles.bodyRowCell}>
        <TextField
          className={styles.noIcon}
          onChange={handleChange}
          value={newDaySetting.reason}
          label='Reason'
          name='reason'
          InputLabelProps={{ shrink: true }}
          sx={
            isWorkingDay
              ? workingDayTextFieldStyles
              : nonWorkingDayTextFieldStyles
          }
        />
      </td>
      <td className={styles.bodyRowCell}>
        <div>
          <TextField
            type='time'
            disabled={!isWorkingDay}
            onChange={handleChange}
            value={!isWorkingDay ? ' ' : newDaySetting.startTime} // The ' ' is a hack to make the time field not move the label down
            name='startTime'
            label='From'
            sx={
              isWorkingDay
                ? workingDayTextFieldStyles
                : nonWorkingDayTextFieldStyles
            }
          />
          <TextField
            type='time'
            disabled={!isWorkingDay}
            onChange={handleChange}
            value={!isWorkingDay ? ' ' : newDaySetting.endTime} // The ' ' is a hack to make the time field not move the label down
            name='endTime'
            label='To'
            sx={
              isWorkingDay
                ? workingDayTextFieldStyles
                : nonWorkingDayTextFieldStyles
            }
          />
        </div>
      </td>
      <td className={styles.bodyRowCell}>
        <TextField
          type='number'
          disabled={!isWorkingDay}
          onChange={handleChange}
          value={
            !isWorkingDay || Number.isNaN(newDaySetting.lunchDuration)
              ? ' '
              : newDaySetting.lunchDuration
          } // The ' ' is a hack to make the time field not move the label down
          label='Lunch Duration'
          sx={
            isWorkingDay
              ? workingDayTextFieldStyles
              : nonWorkingDayTextFieldStyles
          }
          name={'lunchDuration'}
        />
      </td>
      <td className={styles.bodyRowCell}>
        <div className={styles.buttonCell}>
          <a
            onClick={props.cancel}
            className={styles.buttonText}
          >
            Cancel
          </a>
          <a
            className={styles.buttonText}
            onClick={addDaySettings}
          >
            Add
          </a>
        </div>
      </td>
    </tr>
  )
}
